/**
 * Loop Guard - Infinite Loop Protection
 *
 * Instruments user code by inserting guard calls into every loop body.
 * When the guard detects too many iterations (across all loops in a single
 * execution context), it throws an error caught by the runtime's try-catch.
 */

export const LOOP_GUARD_BUDGET = 10_000_000
export const LOOP_GUARD_FN_NAME = '__fc0_guard'
export const LOOP_GUARD_RESET_FN_NAME = '__fc0_resetGuard'

/**
 * Transform user code to insert guard calls into every loop body.
 *
 * Walks the code character-by-character, skipping strings, comments,
 * and template literals. When it finds `while`, `for`, or `do` keywords
 * (verified as actual keywords), it inserts a guard call into the loop body.
 */
export function instrumentLoops(code: string): string {
  const result: string[] = []
  let i = 0
  const len = code.length

  while (i < len) {
    // Skip single-line comments
    if (code[i] === '/' && code[i + 1] === '/') {
      const start = i
      i += 2
      while (i < len && code[i] !== '\n') i++
      result.push(code.slice(start, i))
      continue
    }

    // Skip multi-line comments
    if (code[i] === '/' && code[i + 1] === '*') {
      const start = i
      i += 2
      while (i < len && !(code[i] === '*' && code[i + 1] === '/')) i++
      i += 2 // skip */
      result.push(code.slice(start, i))
      continue
    }

    // Skip string literals (single quote, double quote)
    if (code[i] === "'" || code[i] === '"') {
      const quote = code[i]
      const start = i
      i++
      while (i < len && code[i] !== quote) {
        if (code[i] === '\\') i++ // skip escaped char
        i++
      }
      i++ // skip closing quote
      result.push(code.slice(start, i))
      continue
    }

    // Skip template literals
    if (code[i] === '`') {
      const start = i
      i++
      let depth = 0
      while (i < len) {
        if (code[i] === '\\') {
          i += 2
          continue
        }
        if (code[i] === '$' && code[i + 1] === '{') {
          depth++
          i += 2
          continue
        }
        if (code[i] === '}' && depth > 0) {
          depth--
          i++
          continue
        }
        if (code[i] === '`' && depth === 0) {
          i++
          break
        }
        i++
      }
      result.push(code.slice(start, i))
      continue
    }

    // Check for loop keywords
    if (matchKeyword(code, i, 'while')) {
      result.push('while')
      i += 5
      i = skipWhitespace(code, i)
      // Skip the condition (...)
      i = skipBalancedParens(code, i, result)
      i = skipWhitespace(code, i, result)
      // Instrument the body
      i = instrumentBody(code, i, result)
      continue
    }

    if (matchKeyword(code, i, 'for')) {
      result.push('for')
      i += 3
      i = skipWhitespace(code, i)
      // Skip the condition (...)
      i = skipBalancedParens(code, i, result)
      i = skipWhitespace(code, i, result)
      // Instrument the body
      i = instrumentBody(code, i, result)
      continue
    }

    if (matchKeyword(code, i, 'do')) {
      result.push('do')
      i += 2
      i = skipWhitespace(code, i, result)
      // Instrument the body immediately
      i = instrumentBody(code, i, result)
      continue
    }

    // Default: copy character
    result.push(code[i])
    i++
  }

  return result.join('')
}

/**
 * Check if position `i` in code matches a keyword that is NOT part of
 * a larger identifier or property access.
 */
function matchKeyword(code: string, i: number, keyword: string): boolean {
  const len = code.length
  const klen = keyword.length

  // Check the keyword chars match
  if (i + klen > len) return false
  for (let j = 0; j < klen; j++) {
    if (code[i + j] !== keyword[j]) return false
  }

  // Character before must not be an identifier char or '.'
  if (i > 0 && isIdentChar(code[i - 1])) return false
  if (i > 0 && code[i - 1] === '.') return false

  // Character after must not be an identifier char
  if (i + klen < len && isIdentChar(code[i + klen])) return false

  return true
}

function isIdentChar(ch: string): boolean {
  return /[a-zA-Z0-9_$]/.test(ch)
}

function skipWhitespace(code: string, i: number, result?: string[]): number {
  const start = i
  while (i < code.length && /\s/.test(code[i])) i++
  if (result && i > start) {
    result.push(code.slice(start, i))
  }
  return i
}

/**
 * Skip balanced parentheses starting at position i.
 * Copies content to result array.
 */
function skipBalancedParens(code: string, i: number, result: string[]): number {
  if (i >= code.length || code[i] !== '(') return i

  let depth = 0
  const start = i
  while (i < code.length) {
    const ch = code[i]

    // Skip strings inside parens
    if (ch === "'" || ch === '"') {
      i++
      while (i < code.length && code[i] !== ch) {
        if (code[i] === '\\') i++
        i++
      }
      i++
      continue
    }

    // Skip template literals inside parens
    if (ch === '`') {
      i++
      let tdepth = 0
      while (i < code.length) {
        if (code[i] === '\\') {
          i += 2
          continue
        }
        if (code[i] === '$' && code[i + 1] === '{') {
          tdepth++
          i += 2
          continue
        }
        if (code[i] === '}' && tdepth > 0) {
          tdepth--
          i++
          continue
        }
        if (code[i] === '`' && tdepth === 0) {
          i++
          break
        }
        i++
      }
      continue
    }

    if (ch === '(') depth++
    if (ch === ')') {
      depth--
      if (depth === 0) {
        i++
        result.push(code.slice(start, i))
        return i
      }
    }
    i++
  }

  // Unbalanced - just push what we have
  result.push(code.slice(start, i))
  return i
}

/**
 * Instrument a loop body starting at position i.
 * If body starts with `{`, inserts guard after `{`.
 * If body is a single statement, wraps it: `{ __fc0_guard(); stmt; }`
 */
function instrumentBody(code: string, i: number, result: string[]): number {
  if (i >= code.length) return i

  if (code[i] === '{') {
    // Braced body: insert guard right after opening brace
    result.push('{')
    result.push(LOOP_GUARD_FN_NAME + '();')
    i++
    // Now we need to copy until the matching closing brace,
    // but we need to recursively handle nested loops
    let depth = 1
    while (i < code.length && depth > 0) {
      // Skip comments
      if (code[i] === '/' && code[i + 1] === '/') {
        const start = i
        i += 2
        while (i < code.length && code[i] !== '\n') i++
        result.push(code.slice(start, i))
        continue
      }
      if (code[i] === '/' && code[i + 1] === '*') {
        const start = i
        i += 2
        while (i < code.length && !(code[i] === '*' && code[i + 1] === '/')) i++
        i += 2
        result.push(code.slice(start, i))
        continue
      }

      // Skip strings
      if (code[i] === "'" || code[i] === '"') {
        const quote = code[i]
        const start = i
        i++
        while (i < code.length && code[i] !== quote) {
          if (code[i] === '\\') i++
          i++
        }
        i++
        result.push(code.slice(start, i))
        continue
      }

      // Skip template literals
      if (code[i] === '`') {
        const start = i
        i++
        let tdepth = 0
        while (i < code.length) {
          if (code[i] === '\\') {
            i += 2
            continue
          }
          if (code[i] === '$' && code[i + 1] === '{') {
            tdepth++
            i += 2
            continue
          }
          if (code[i] === '}' && tdepth > 0) {
            tdepth--
            i++
            continue
          }
          if (code[i] === '`' && tdepth === 0) {
            i++
            break
          }
          i++
        }
        result.push(code.slice(start, i))
        continue
      }

      // Check for nested loops
      if (matchKeyword(code, i, 'while')) {
        result.push('while')
        i += 5
        i = skipWhitespace(code, i)
        i = skipBalancedParens(code, i, result)
        i = skipWhitespace(code, i, result)
        i = instrumentBody(code, i, result)
        continue
      }
      if (matchKeyword(code, i, 'for')) {
        result.push('for')
        i += 3
        i = skipWhitespace(code, i)
        i = skipBalancedParens(code, i, result)
        i = skipWhitespace(code, i, result)
        i = instrumentBody(code, i, result)
        continue
      }
      if (matchKeyword(code, i, 'do')) {
        result.push('do')
        i += 2
        i = skipWhitespace(code, i, result)
        i = instrumentBody(code, i, result)
        continue
      }

      if (code[i] === '{') depth++
      if (code[i] === '}') {
        depth--
        if (depth === 0) {
          result.push('}')
          i++
          return i
        }
      }

      result.push(code[i])
      i++
    }
    return i
  }

  // Single-statement body (no braces): wrap with braces and add guard
  // Read until semicolon or end of statement
  result.push('{ ')
  result.push(LOOP_GUARD_FN_NAME + '(); ')

  // We need to find the end of the single statement.
  // A statement ends at `;` (respecting nesting), or could be
  // another control structure.
  i = copySingleStatement(code, i, result)

  result.push(' }')
  return i
}

/**
 * Copy a single statement from position i, handling nested braces/parens.
 * Returns position after the statement.
 */
function copySingleStatement(
  code: string,
  i: number,
  result: string[],
): number {
  // Handle block as single statement (e.g., `while(x) if(y) { ... }`)
  if (i < code.length && code[i] === '{') {
    let depth = 0
    while (i < code.length) {
      if (code[i] === '{') depth++
      if (code[i] === '}') {
        depth--
        if (depth === 0) {
          result.push(code[i])
          i++
          return i
        }
      }
      result.push(code[i])
      i++
    }
    return i
  }

  // Regular statement: copy until semicolon (at depth 0)
  let parenDepth = 0
  while (i < code.length) {
    const ch = code[i]

    // Skip strings
    if (ch === "'" || ch === '"') {
      const quote = ch
      const start = i
      i++
      while (i < code.length && code[i] !== quote) {
        if (code[i] === '\\') i++
        i++
      }
      i++
      result.push(code.slice(start, i))
      continue
    }

    // Skip template literals
    if (ch === '`') {
      const start = i
      i++
      let tdepth = 0
      while (i < code.length) {
        if (code[i] === '\\') {
          i += 2
          continue
        }
        if (code[i] === '$' && code[i + 1] === '{') {
          tdepth++
          i += 2
          continue
        }
        if (code[i] === '}' && tdepth > 0) {
          tdepth--
          i++
          continue
        }
        if (code[i] === '`' && tdepth === 0) {
          i++
          break
        }
        i++
      }
      result.push(code.slice(start, i))
      continue
    }

    if (ch === '(') parenDepth++
    if (ch === ')') parenDepth--

    result.push(ch)
    i++

    if (ch === ';' && parenDepth === 0) return i
  }

  return i
}
